import {Alignment, Catalog, FormattedDate} from './common';
import {Wizard} from '../../wizard/types/mx/catalog';
import {FinancedQuote, TermPayments} from '../../quote/types/mx/reducers';
import {Accept} from 'react-dropzone';
import {MainMenuState} from '../../admin/types/mx/reducers';

export type KeyValue = {
    key: number | string;
    title: string;
    description?: string;
    type?: boolean;
};

export interface AlertProps {
    alertList?: KeyValue[];
    removeAlert: Function;
}

export interface PayWarrantyProps {
    booleanOptions: OptionProps[];
    cardNumber: FormFieldProps<string>;
    ccv: FormFieldProps<string>;
    cardName: FormFieldProps<string>;
    cardExpirationDate: FormFieldProps<string>;
    ccvMaxLenght: number;
    ccvText: string;
    payType: string;
    warrantyId: string;
    labelText: FormFieldProps<string>;
    amountText: FormFieldProps<string>;
    hasPayWarrantyFieldsError: boolean;
    paid: boolean;
    cancelled: boolean;
}

export interface QuoteProps {
    brand: string;
    model: string;
    version: string;
    year: string;
    price: string;
    downPayment: string;
    financedInsuranceFinanced: string;
    financedInsuranceCash: string;
    includeWarranty: boolean;
    warranty: string;
    financingAmountFinanced: string;
    financingAmountCash: string;
    termPayments: TermPayments[];
    cashInsuranceFinanced: string;
    cashInsuranceCash: string;
    commissionAmountFinanced: string;
    commissionAmountCash: string;
    bankDisplayName: string;
    bankLogoUrl: string;
    createdAt: string;
    expirationDate: string;
    quoteId: string;
}

export interface AlertItemProps {
    alertText: string;
    alertTitle: string;
    alertType: boolean;
    removeAlert: Function;
}

export interface WarningBannerProps {
    show: boolean;
    message: string;
    variant?: 'warning' | 'success';
}

type ButtonType = 'default' | 'outlined' | 'link' | 'small-outlined' | 'squared' | 'circle-small-outlined';

export interface ButtonProps {
    text?: string;
    onPress?: Function;
    type?: ButtonType;
    disabled?: boolean;
    customClass?: string;
    role?: string;
    href?: string;
    target?: string;
    icon?: string;
}

export class ButtonState {
    buttonStyle: string;
}

export interface MobileMenuProps {
    assistantInformation?: Wizard;
    onPressModalAssistant?: Function;
    onClickLogout: Function;
}

export class MobileMenuState {
    showMenu: boolean;
}

export interface InputTextProps {
    id: string;
    label?: string;
    pattern?: any;
    value?: any;
    helpAction?: any;
    required?: boolean;
    disabled?: boolean;
    hasError?: boolean;
    labelError?: string;
    helpText?: any;
    align?: Alignment;
    onChange?: Function;
    onClick?: Function;
    onPressEnter?: Function;
    placeholder?: string;
    name?: string;
    minLength?: number;
    maxLength?: number;
    maxDecimals?: number;
    autocomplete?: string;
    inputCustomClass?: string;
    containerCustomClass?: string;
    type?: string;
    minValue?: number;
    readOnly?: boolean;
    showCurrencySymbol?: boolean;
    showPercentSymbol?: boolean;
    acceptLeadingZeros?: boolean;
}

export interface InputTextState {
    isEditing: boolean;
}

export interface InputCreditCardProps {
    id: string;
    label?: string;
    value?: any;
    required?: boolean;
    hasError?: boolean;
    labelError?: string;
    helpText?: any;
    align?: Alignment;
    onChange?: Function;
    placeholder?: string;
    name?: string;
    minLength?: number;
    maxDecimals?: number;
    autocomplete?: string;
    inputCustomClass?: string;
    containerCustomClass?: string;
    minValue?: number;
}

export interface CheckboxOptionProps {
    value: any;
    label: any;
    defaultChecked: boolean;
}

export interface CheckboxProps {
    id: string;
    name: string;
    customClassLabel?: string;
    onChange: Function;
    options: CheckboxOptionProps[];
    customClass?: string;
    labelError?: string;
    required?: boolean;
    listGroupClass?: string;
    orientation?: string;
    customClassWrapper?: string;
    label?: string;
    superscriptLabel?: string;
    hasError: boolean;
}

interface CatalogDateState {
    id: string;
    value: string;
}

export interface TimePickerState {
    hours: CatalogDateState[];
    minutes: CatalogDateState[];
    hourSelected: string;
    minuteSelected: string;
}

export interface TimePickerProps {
    label: string;
    id: string;
    value: any;
    required: boolean;
    hasError: boolean;
    labelError: string;
    onChange: Function;
    name?: string;
    rangeHours?: number;
    intervalMinutes?: number;
}

export interface DatePickerState {
    days: CatalogDateState[];
    months: CatalogDateState[];
    years: CatalogDateState[];
    daySelected: string;
    monthSelected: string;
    yearSelected: string;
    shouldBeGreaterThanCurrent: boolean
}

export interface DatePickerCreditCardState {
    days: CatalogDateState[];
    months: CatalogDateState[];
    years: CatalogDateState[];
    daySelected: string;
    monthSelected: string;
    yearSelected: string;
}

export interface DatePickerProps {
    label: string;
    id: string;
    value: any;
    required: boolean;
    hasError: boolean;
    labelError: string;
    onChange: Function;
    minYearAllowed?: number;
    minAgeAllowed?: number;
    maxYearAllowed?: number;
    name?: string;
    rangeYears?: number;
    showDay?: boolean;
    showMonth?: boolean;
    showYear?: boolean;
    shouldBeGreaterThanCurrent?: boolean;
}

export interface DatePickerCreditCardProps {
    label: string;
    id: string;
    value: any;
    required: boolean;
    hasError: boolean;
    labelError: string;
    onChange: Function;
    minYearAllowed?: number;
    minAgeAllowed?: number;
    name?: string;
    rangeYears?: number;
    showDay?: boolean;
    showMonth?: boolean;
    showYear?: boolean;
}

export interface DateCalendarPickerProps {
    label: string;
    id: string;
    value: any;
    required: boolean;
    hasError: boolean;
    disabled: boolean;
    labelError: string;
    onChange?: Function;
    name?: string;
    maxDate?: Date;
    todayButton?: boolean;
}

export interface DateCalendarPickerState {
    value: Date;
}

export interface CatalogDropdownState {
    id: string;
    value: string;
}

export interface DropdownProps {
    id: string;
    value: any;
    required: boolean;
    hasError: boolean;
    labelError: string;
    onChange: Function;
    name?: string;
    label?: string;
    options?: CatalogDropdownState[];
}

export interface SelectSearchState {
    filterItems: Catalog[];
    inputText: string;
    showSelector?: boolean;
    isLoading: boolean;
}

export interface SearchState {
    filterItems: SearchData[];
    inputText: string;
    showSelector?: boolean;
    isLoading: boolean;
}

export interface SearchData {
    id: string;
    value: string;
    payload?: any;
}

export interface SelectSearchProps {
    id: string;
    value: Catalog;
    required: boolean;
    hasError: boolean;
    labelError: string;
    onChange: Function;
    name?: string;
    label?: string;
    placeholder?: string;
    customClass?: string;
    insertInput?: boolean;
    filter?: boolean;
    showMissingCatalog?: boolean;
    disabled: boolean;
    inputType?: string;
    data?: Catalog[];
    onInputChange?: Function;
    helpAction?: any;
    showIconWarning: boolean;
}

export interface SearchProps {
    id: string;
    value: SearchData;
    onChange: Function;
    name?: string;
    label?: string;
    placeholder?: string;
    customClass?: string;
    insertInput?: boolean;
    filter?: boolean;
    inputType?: string;
    data?: Catalog[];
    onInputChange?: Function;
    onCleanSearchQuery?: () => void;
}

export interface SwitchProps {
    id: string;
    label: string;
    value: boolean;
    disabled?: boolean;
    onChange: (status: boolean, error: string) => void;
    hasError?: boolean;
    labelError?: string;
}

export interface SelectMultipleProps {
    id: string;
    label: string;
    placeholder?: string;
    value: any;
    required?: boolean
    hasError?: boolean;
    labelError?: string;
    onChange: (action: SelectMultipleAction, error: string) => void;
    data: any;
    noOptionsMessage?: () => string;
}

export enum SelectMultipleActionTypesProps {
    'select-option' = 'select-option',
    'deselect-option' = 'deselect-option',
    'pop-value' = 'pop-value',
    'clear' = 'clear',
    'create-option' = 'create-option',
    'remove-value' = 'remove-value'
}

export interface SelectMultipleOption extends Catalog {
    label: string;
}

export interface SelectMultipleAction {
    action: keyof typeof SelectMultipleActionTypesProps,
    option?: SelectMultipleOption
    removedValue?: SelectMultipleOption
}

export interface TableSelectCellProps {
    id: string;
    value: Catalog;
    onChange: Function;
    name?: string;
    placeholder?: string;
    customClass?: string;
    inputCustomClass?: string;
    data?: Catalog[];
}

export interface TableSelectCellState {
    showSelector?: boolean;
}

export interface FileProps {
    fileName: string;
    mimeType: string;
    originalUrl: string;
    compressUrl: string;
    thumbnailUrl: string;
    id: string;
    uploadDate?: FormattedDate;
}

export interface FileUploaderProps {
    id: string;
    type: string;
    section: string;
    label: string;
    multiple: boolean;
    onDrop: Function;
    onDelete: Function;
    uploadedFiles: FileProps[];
    onClickThumbnail: Function;
    hasError: boolean;
    labelError: string;
    required?: boolean;
    customClass?: string;
    hideDelete?: boolean;
    accept?: Accept;

}

type OptionsListGroupClass = 'list-group-borderless' | 'list-group-flush';

export interface OptionProps {
    id: string;
    value: any;
    label: string;
    defaultChecked?: boolean;
}

export interface MonthlyPaymentOptionProps {
    id: string;
    value: any;
    label: string;
    labelSh3: string;
    defaultChecked?: boolean;
}

export interface OptionsProps {
    id: string;
    options: OptionProps[];
    onChange: Function;
    listGroupClass?: OptionsListGroupClass;
    defaultValue?: any;
    name: string;
    customClassLabel?: string;
    required?: boolean;
    labelError?: string;
    hasError?: boolean;
    label?: string;
    customClass?: string;
    orientation?: string;
    customWrapperClass?: string;
}

export interface OptionsMonthlyProps {
    id: string;
    options: OptionProps[];
    onChange: Function;
    defaultValue?: any;
    name: string;
    customClassLabel?: string;
    required?: boolean;
    labelError?: string;
    hasError?: boolean;
    label?: string;
}

export interface OptionInsuranceProps {
    icon: string;
    name: string;
    target: string;
    insuranceValueByYearValue: string;
    insuranceValueByYear: string;
    onShowInfo: () => void;
}

export interface OptionsInsuranceProps {
    id: string;
    name: string;
    options: OptionInsuranceProps[];
    required: boolean;
    insuranceSelectedTarget: string;
    onChange: Function;
    customClass?: string;
    hasError?: boolean;
    labelError?: string;
}

export interface OptionMonthlyKunaProps {
    id: string;
    term: number;
    value: string;
}

export interface OptionsMonthlyKunaProps {
    id: string;
    name: string;
    options: OptionMonthlyKunaProps[];
    required: boolean;
    monthlySelectedTarget: number;
    onChange: Function;
    customClass?: string;
    hasError?: boolean;
    labelError?: string;
    label?: string;
}

export class FormFieldProps<T> {
    value: T;
    error: string;

    constructor(valueParam: T, errorParam: string = null) {
        this.value = valueParam;
        this.error = errorParam;
    }
}

export interface MenuLoanApplicationItem {
    id: string;
    icon: string;
    iconSelected: string;
    label: string;
    shortLabel: string;
    labelError: string;
    hasError: boolean;
    selected: boolean;
    onPress?: () => void;
    allowedRoles?: string[];
}

export interface LoanApplicationMenu {
    id: string;
    icon: string;
    iconSelected: string;
    label: string;
    hovered: boolean;
    selected: boolean;
    read: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onPress?: () => void;
    items: MenuLoanApplicationItem[];
    allowedRoles?: string[];
}

export interface SimpleMenu {
    id: string;
    label: string;
    hovered: boolean;
    selected: boolean;
    onMouseEnterMenu?: () => void;
    onMouseLeaveMenu?: () => void;
    onPressMenu?: () => void;
}

export interface MainMenu {
    id: string;
    icon: string;
    label: string;
    path: string;
    allowedRoles: string[];
    selected: boolean;
    isNew?: boolean;
    onPress?: () => void;
    children?: MainMenuState;
}

export interface VerticalMenuProps {
    items: MenuLoanApplicationItem[];
}

export interface VerticalMenuItemProps {
    id: string;
    onPress: () => void;
    icon: string;
    iconSelected: string;
    shortLabel: string;
    label: string;
    selected: boolean;
}

export interface MenuViewProps {
    menu: LoanApplicationMenu[];
    onMouseEnterMainMenu: () => void;
    onMouseLeaveMainMenu: () => void;
}

export interface SimpleMenuViewProps {
    menu: SimpleMenu[];
    filters?: any;
}

export interface SubMenuViewProps {
    visible: boolean;
    items: MenuLoanApplicationItem[];
}

export interface InfiniteScrollProps {
    dataLength: number;
    next: Function;
    hasMore: boolean;
    loader: any;
    scrollableTarget: any;
    scrollThreshold: number;
    endMessage: any;
    pullDownToRefreshThreshold?: number;
    height?: number;
    initialScrollY?: number;
    pullDownToRefresh?: boolean;
    hasChildren?: boolean;
    refreshFunction?: Function;
    onScroll?: Function;
    children?: any;
    pullDownToRefreshContent?: any;
    releaseToRefreshContent?: any;
    style?: object;
}

export interface InfiniteScrollState {
    showLoader: boolean;
    actionTriggered: boolean;
    pullToRefreshThresholdBreached: boolean;
    lastScrollTop: number;
}

export interface NoInternetState {
    isConnected: boolean;
}

export interface DropZoneProps {
    id: string
    required: boolean
    hasError: boolean
    labelError: string
    label: string
    labelButton: string
    customClass?: string
    mimeTypesAccepted?: Accept
    maxSize?: number
    multiple?: boolean
    onDrop?: Function
    onDragLeave?: Function
    onDragOver?: Function
    onDragEnter?: Function
    onSelectFile?: Function
}

export interface DropZoneState{
    isDragging: boolean
}

export interface InputRangeProps {
    id: string
    min: number
    max: number
    step:number
    colorSelected: string
    colorBackGround: string
    position: string
    defaultValue: string
    minLength: number
    maxLength: number
    disabled: boolean
    label: string
    hasError: boolean
    labelError: string
    required: boolean
    inputCustomClass?: string;
    helpText?: any;
    isCurrency: boolean,
    countryCurrency?: string
    showValueOnMouseUp: boolean
    showHelpText?: boolean
    onChangeBox: Function
}

export interface InputRangeState {
    isFocus: boolean
}

export interface MarkerProps {
    icon: string;
}

export interface ExpandableTextProps {
    text: string;
    maxLines?: number;
}

export interface ExpandableTextState {
    isExpanded: boolean;
    isOverflowing: boolean;
}
